const SVG_NS = 'http://www.w3.org/2000/svg';

// Serialize the entire chart, independent of viewport size and scrolling.
export function createChartImageSvg(element: HTMLElement, title: string, subtitle: string, background: string, foreground: string): SVGSVGElement {
  const chart = element.querySelector<SVGSVGElement>('svg.recharts-surface');
  if (!chart) throw new Error('Chart is not ready');
  const width = Number(chart.getAttribute('width')) || chart.getBoundingClientRect().width;
  const height = Number(chart.getAttribute('height')) || chart.getBoundingClientRect().height;
  if (width <= 0 || height <= 0) throw new Error('Chart has no dimensions');
  const imageWidth = Math.max(width + 48, 800);
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', String(imageWidth));
  svg.setAttribute('height', String(height + 120));
  svg.setAttribute('viewBox', `0 0 ${imageWidth} ${height + 120}`);
  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('width', '100%');
  rect.setAttribute('height', '100%');
  rect.setAttribute('fill', background);
  svg.appendChild(rect);
  const addText = (value: string, y: number, size: number) => {
    const text = document.createElementNS(SVG_NS, 'text');
    text.textContent = value;
    text.setAttribute('x', '24');
    text.setAttribute('y', String(y));
    text.setAttribute('fill', foreground);
    text.setAttribute('font-family', 'Arial, sans-serif');
    text.setAttribute('font-size', String(size));
    // Long titles remain inside the image without clipping.
    if (value.length * size * 0.6 > imageWidth - 48) {
      text.setAttribute('textLength', String(imageWidth - 48));
      text.setAttribute('lengthAdjust', 'spacingAndGlyphs');
    }
    svg.appendChild(text);
  };
  addText(title, 32, 20);
  addText(subtitle, 60, 13);
  const clone = chart.cloneNode(true) as SVGSVGElement;
  const originals = [chart, ...Array.from(chart.querySelectorAll<SVGElement>('*'))];
  const copies = [clone, ...Array.from(clone.querySelectorAll<SVGElement>('*'))];
  // Recharts relies on inherited fonts and CSS; embed those styles for standalone images.
  originals.forEach((original, index) => {
    const computed = window.getComputedStyle(original);
    ['font-family', 'font-size', 'font-weight', 'fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-dasharray', 'opacity', 'text-anchor', 'dominant-baseline'].forEach(property => {
      const value = computed.getPropertyValue(property);
      if (value) copies[index].style.setProperty(property, value);
    });
    // Browser-computed clip paths can contain an absolute page URL.
    const clip = original.getAttribute('clip-path');
    if (clip) copies[index].setAttribute('clip-path', clip.replace(/url\([^#]*#([^)'"\s]+)['"]?\)/g, 'url(#$1)'));
  });
  clone.setAttribute('x', '24');
  clone.setAttribute('y', '80');
  svg.appendChild(clone);
  return svg;
}

export async function downloadChartImage(element: HTMLElement, title: string, subtitle: string, filename: string, format: 'png' | 'svg', background: string, foreground: string): Promise<void> {
  await document.fonts?.ready;
  const svg = createChartImageSvg(element, title, subtitle, background, foreground);
  const svgBlob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' });
  let blob = svgBlob;
  if (format === 'png') {
    const url = URL.createObjectURL(svgBlob);
    try {
      const image = new Image();
      image.src = url;
      await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error('Image rendering failed')); });
      const canvas = document.createElement('canvas');
      const width = Number(svg.getAttribute('width'));
      const height = Number(svg.getAttribute('height'));
      // Use 2x resolution where canvas limits permit. Never crop tall charts.
      const scale = Math.min(2, 16000 / Math.max(width, height), Math.sqrt(64000000 / (width * height)));
      canvas.width = Math.ceil(width * scale);
      canvas.height = Math.ceil(height * scale);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas unavailable');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(result => result ? resolve(result) : reject(new Error('Image encoding failed')), 'image/png'));
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  const safeFilename = Array.from(filename, character => character.charCodeAt(0) < 32 ? '_' : character).join('').replace(/[<>:"/\\|?*]/g, '_');
  anchor.download = `${safeFilename}.${format}`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
